import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../config/firebaseConfig';
import { UserAuth } from '../../context/authContext';
import { handleMinerLeave } from '../../utils/astrapos/edgeCaseHandlers';
import BlockProgress from './BlockProgress';
import RoleDisplay from './RoleDisplay';

// Embedded components
import CreateTranscation from '../CreateTranscation';
import EtherScan from '../etherScan';
import Mempool from '../Mempool';
import MyTranscation from '../MyTranscation';
import Transcation from '../Transcation';
import UserInfoCard from '../UserInfoCard';

// Section divider component
const SectionDivider = () => (
  <div className="my-8 border-t-2 border-purple-500/20"></div>
);

/**
 * PLAYING stage: Main game phase where players perform role-based actions
 * Unified single-scrollable page showing role-based features
 */
const PlayingStage = ({
  roomId,
  roomData,
  userData,
  role,
  targetBlocks = 5,
}) => {
  const [minerStillPresent, setMinerStillPresent] = useState(true);
  const { user } = UserAuth();

  // Monitor for miner leaving
  useEffect(() => {
    if (!roomId || role !== 'validator') return;

    const roomRef = doc(db, 'rooms', roomId);
    const unsubscribe = onSnapshot(roomRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();

        // Check if current miner is still in miners array
        if (!data.miners?.includes(userData.publicKey)) {
          setMinerStillPresent(false);
          // Attempt to reassign miner
          handleMinerLeave(roomId);
        }
      }
    });

    return () => unsubscribe();
  }, [roomId, userData.publicKey, role]);

  const isWinner = roomData.stakingWinner === userData.publicKey;
  const blocksMinedThisCycle = roomData.gameState?.blocksMinedThisCycle || 0;

  // If user was miner but no longer is, show message
  if (role === 'validator' && !minerStillPresent) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Validator Role Reassigned
          </h1>
        </div>

        <div className="bg-orange-500/10 backdrop-blur rounded-xl p-8 border border-orange-500/20">
          <p className="text-orange-200 text-center">
            You are no longer the Validator. The role has been reassigned to
            another player. You can continue as a participant in this round.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">Game Phase</h1>
        <p className="text-white/60">
          Validate {targetBlocks} blocks to complete this round
        </p>
      </div>

      {/* Role Display */}
      <RoleDisplay role={role} isWinner={isWinner} />

      {/* Block Progress */}
      <BlockProgress current={blocksMinedThisCycle} target={targetBlocks} />

      {/* VALIDATOR SPECIFIC SECTIONS */}
      {role === 'validator' && (
        <>
          <SectionDivider />

          {/* Mempool Section */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Mempool</h2>
            <Mempool embedded={true} />
          </div>

          <SectionDivider />

          {/* Block Validation Section */}
          {/* <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">
              Block Validation
            </h2>
            <BlocksTable embedded={true} />
          </div> */}
        </>
      )}

      {/* PARTICIPANT SPECIFIC SECTIONS */}
      {role === 'participant' && (
        <>
          <SectionDivider />

          {/* User Info Card */}
          <UserInfoCard roomId={roomId} userData={userData} />

          <SectionDivider />

          {/* Create Transaction Section */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">
              Create Transaction
            </h2>
            <CreateTranscation
              user={user}
              userData={userData}
              roomId={roomId}
            />
          </div>

          <SectionDivider />

          {/* My Transactions Section */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">My Transactions</h2>
            <MyTranscation embedded={true} />
          </div>
        </>
      )}

      {/* COMMON SECTIONS - Available to All Players */}
      <SectionDivider />

      {/* All Transactions Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-white">All Transactions</h2>
        <Transcation embedded={true} />
      </div>

      <SectionDivider />

      {/* Network Explorer Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-white">Network Explorer</h2>
        <EtherScan embedded={true} />
      </div>

      {/* Info Card */}
      <SectionDivider />
      <div className="bg-blue-500/10 backdrop-blur rounded-xl p-6 border border-blue-500/20">
        <p className="text-blue-200 text-sm">
          <strong>Game Progress:</strong> Complete the validation of{' '}
          {targetBlocks} blocks to finish this round and proceed to the
          leaderboard. All features are available on this page based on your
          assigned role.
        </p>
      </div>
    </div>
  );
};

export default PlayingStage;
